import React, { useState, useRef } from 'react';
import { FormikProps } from 'formik';
import { toString, toNumber, isNil } from 'lodash';
import { Button, Modal, Tabs } from 'antd';

import { TabValidationStatuses } from 'enums';
import useTabs from 'hooks/useTabs';
import BaseAlert from 'components/wrappers/BaseAlert';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import { BaseModal, BaseModalFooter } from 'components/wrappers/BaseModal';
import { updateClientAdditionalInfo } from 'redux/reducers/clientSlice';
import { ResourceDownloadResponse } from 'api/backofficeApiClient';
import { CustomerAdditionalInfoModel } from 'api/adminApiClient';
import { getTabValidationIcon } from 'utils/tabUtils';
import GeneralContractInfo, {
  GeneralContractInfoFormValues,
} from './GeneralContractInfo';
import FinancialInfo, { FinancialInfoFormValues } from './FinancialInfo';
import AssignmentRequirements, {
  AssignmentRequirementsFormValues,
} from './AssignmentRequirements';
import { useAppDispatch } from 'hooks/useAppDispatch';

export interface EditClientAdditionalInfoInitialValues {
  generalContractInfo: GeneralContractInfoFormValues;
  financialInfo?: FinancialInfoFormValues;
  assignmentRequirements?: AssignmentRequirementsFormValues;
}

type Props = {
  visible: boolean;
  customerId: string;
  initialValues?: EditClientAdditionalInfoInitialValues;
  certificateFileInfo?: ResourceDownloadResponse;
  tempToPermConversionFileInfo?: ResourceDownloadResponse;
  purchaseOrderFile?: ResourceDownloadResponse;
  sowTemplateFileInfo?: ResourceDownloadResponse;
  onboardingDocumentationFileInfo?: ResourceDownloadResponse[];
  onClose: () => void;
  onSubmit?: () => void;
};

enum TabKeys {
  GeneralContactInfo = 0,
  FinancialInfo = 1,
  AssignmentRequirements = 2,
}

const getInitialTabs = () => [
  {
    title: 'General Contract Information',
    status: TabValidationStatuses.Success,
  },
  {
    title: 'Financial Information',
    status: TabValidationStatuses.Success,
  },
  {
    title: 'Additional information 3',
    status: TabValidationStatuses.Success,
  },
];

type GeneralContractInfoFormikProps =
  FormikProps<GeneralContractInfoFormValues>;
type FinancialInfoFormikProps = FormikProps<FinancialInfoFormValues>;
type AssignmentRequirementsFormikProps =
  FormikProps<AssignmentRequirementsFormValues>;

export default function EditClientAdditionalInfoModal(props: Props) {
  const {
    visible,
    customerId,
    initialValues,
    certificateFileInfo,
    tempToPermConversionFileInfo,
    purchaseOrderFile,
    onboardingDocumentationFileInfo,
    sowTemplateFileInfo,
    onClose,
    onSubmit,
  } = props;
  const dispatch = useAppDispatch();
  const [activeTab, setActiveTab] = useState(TabKeys.GeneralContactInfo);
  const [tabs, onFormChange] = useTabs(getInitialTabs());
  const [isLoading, setIsLoading] = useState(false);

  const generalContractInfoFormRef: any = useRef();
  const financialInfoFormRef: any = useRef();
  const assignmentRequirementsFormRef: any = useRef();

  const handleTabChange = (activeTab: string) =>
    setActiveTab(toNumber(activeTab));

  const handleNextStep = () => setActiveTab((step) => step + 1);
  const handlePrevStep = () => setActiveTab((step) => step - 1);

  const isFormValid = () => {
    return [
      generalContractInfoFormRef.current?.isValid,
      financialInfoFormRef.current?.isValid,
      assignmentRequirementsFormRef.current?.isValid,
    ].every(Boolean);
  };

  const isFormSubmitting = () => {
    return [
      generalContractInfoFormRef.current?.isSubmitting,
      financialInfoFormRef.current?.isSubmitting,
      assignmentRequirementsFormRef.current?.isSubmitting,
    ].every(Boolean);
  };

  const isFormDirty = () => {
    return [
      generalContractInfoFormRef.current?.dirty,
      financialInfoFormRef.current?.dirty,
      assignmentRequirementsFormRef.current?.dirty,
    ].some(Boolean);
  };

  const resetFormSubmitting = () => {
    [
      generalContractInfoFormRef.current,
      financialInfoFormRef.current,
      assignmentRequirementsFormRef.current,
    ].forEach((form) => form.setSubmitting(false));
  };

  const handleModalCancel = () => {
    if (isFormSubmitting()) return;

    if (isFormDirty()) {
      return Modal.confirm({
        title: 'Are you sure? The data will not be saved.',
        icon: <span className="icon icon-attention-filled" />,
        onOk() {
          onClose();
        },
        onCancel() {},
      });
    }
    onClose();
  };

  const handleSubmit = async () => {
    const generalContractInfoForm: GeneralContractInfoFormikProps =
      generalContractInfoFormRef.current;
    const financialInfoForm: FinancialInfoFormikProps =
      financialInfoFormRef.current;
    const assignmentRequirementsForm: AssignmentRequirementsFormikProps =
      assignmentRequirementsFormRef.current;

    await Promise.all([
      generalContractInfoForm.submitForm(),
      financialInfoForm.submitForm(),
      assignmentRequirementsForm.submitForm(),
    ]);

    if (!isFormValid()) return;

    const {
      specifications,
      submittedTo,
      certificate,
      isCreditCheckVerified,
      sowTemplate,
      sowRequired,
      sowSpecialInstructions,
    } = generalContractInfoForm.values;

    const {
      creditCheck,
      commissionType,
      notes,
      paymentTerms,
      markupRate,
      otRateMultiplier,
      dtBillRateMultiplier,
      conversion,
      MSPFee,
      VMSFee,
      invoicingInfo,
      invoicingInstructions,
      invoicingCycle,
      payrollCycle,
      url,
      credentials,
      providerName,
      isThirdWorkforce,
      purchaseOrder,
      purchaseOrderInstructions,
      purchaseOrderFileKey,
    } = financialInfoForm.values;

    const {
      contractorsPermitted,
      preScreeningRequired,
      preScreeningBilledTo,
      preScreening,
      designatedPreScreening,
      isOnboardingDocumentationRequired,
      specialInstructions,
      onboardingDocumentation,
      terminationInstructions,
      workPerformance,
      tempToPermConversion,
      timeEntry,
      assignmentNotes,
    } = assignmentRequirementsForm.values;

    const data: CustomerAdditionalInfoModel = {
      generalContractInformation: {
        insuranceRequirements: {
          certificateOfInsuranceSpecifications:
            specifications.trim() || undefined,
          submittedTo: submittedTo.trim() || undefined,
          certificateOfInsuranceCreatedFileKey: certificate?.length
            ? certificate[0]
            : undefined,
          isCreditCheckVerified: isCreditCheckVerified,
          sowRequired: sowRequired,
          sowTemplateFileKey: sowTemplate?.length ? sowTemplate[0] : undefined,
          sowSpecialInstructions: sowSpecialInstructions.trim() || undefined,
        },
      },
      financialInformation: {
        financialOverview: {
          creditCheckScore: creditCheck.trim() || undefined,
          commissionTypeApproval: commissionType || undefined,
          addedNotes: notes.trim() || undefined,
          paymentTerm: paymentTerms || undefined,
        },
        rateOverview: {
          fixedMarkupRate: markupRate.trim() || undefined,
          otRateMultiplier: otRateMultiplier.trim() || undefined,
          dtBillRateMultiplier: !isNil(dtBillRateMultiplier)
            ? dtBillRateMultiplier
            : undefined,
          tempToPermConversionFileKey: conversion?.length
            ? conversion[0]
            : undefined,
        },
        mspOrVmsOverview: {
          mspFee: !isNil(MSPFee) ? MSPFee : undefined,
          vmsFee: !isNil(VMSFee) ? VMSFee : undefined,
          invoicingInformation: invoicingInfo.trim() || undefined,
          specialInvoicingInstructions:
            invoicingInstructions.trim() || undefined,
          invoicingCycle: invoicingCycle || undefined,
          payrollCycle: payrollCycle || undefined,
          isThereThirdPartyWorkforce: !isNil(isThirdWorkforce)
            ? isThirdWorkforce
            : undefined,
          url: url || undefined,
          credentials: credentials || undefined,
          mspOrVmsProviderName: providerName || undefined,
          purchaseOrder: purchaseOrder,
          purchaseOrderFileKey: purchaseOrderFileKey?.length
            ? purchaseOrderFileKey[0]
            : undefined,
          purchaseOrderInstructions: purchaseOrderInstructions || undefined,
        },
      },
      additionalInfo3: {
        assignmentRequirements: {
          isIndependentContractors: contractorsPermitted,
          isPreScreeningRequired: preScreeningRequired,
          preScreeningBilledTo,
          preScreening: preScreening.trim() || undefined,
          isOnboardingDocumentationRequired: isOnboardingDocumentationRequired,
          designatedPreScreeningProvider: designatedPreScreening,
          onboardingDocumentationFileKeys:
            onboardingDocumentation.length && isOnboardingDocumentationRequired
              ? onboardingDocumentation
              : undefined,
          specialInstructions: specialInstructions.trim() || undefined,
          terminationInstructions: terminationInstructions.trim() || undefined,
        },
        specialAssignmentRequirements: {
          guaranteedWorkPerformance: workPerformance.trim() || undefined,
          tempToPermConversion: tempToPermConversion.trim() || undefined,
          timeEntry: timeEntry.trim() || undefined,
          addedNotes: assignmentNotes.trim() || undefined,
        },
      },
    };

    setIsLoading(true);
    const result = await dispatch(
      updateClientAdditionalInfo({ customerId, data }),
    );
    setIsLoading(false);

    if (updateClientAdditionalInfo.fulfilled.match(result)) {
      BaseAlert.success('Customer has been updated.');
      onClose();

      onSubmit?.();
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);

      resetFormSubmitting();
    }
  };

  return (
    <BaseModal
      title="Edit customer additional information"
      maxWidth={888}
      open={visible}
      onCancel={handleModalCancel}
      className="modal-with-tabs"
    >
      <Tabs
        activeKey={toString(activeTab)}
        onChange={(activeKey: string) => handleTabChange(activeKey)}
        animated={false}
        className="icon-tabs modal-scroll-wrapper"
      >
        <Tabs.TabPane
          key={toString(TabKeys.GeneralContactInfo)}
          tab={
            <div
              className="tab-pane-wrapper"
              data-id="editCustomerAdditionInfoFinancialTab"
            >
              <span className="tab-title">
                {tabs[TabKeys.GeneralContactInfo].title}
              </span>
              {getTabValidationIcon(tabs[TabKeys.GeneralContactInfo].status)}
            </div>
          }
        >
          <GeneralContractInfo
            formRef={generalContractInfoFormRef}
            certificateFileInfo={certificateFileInfo}
            initialValues={initialValues?.generalContractInfo}
            onChange={onFormChange[TabKeys.GeneralContactInfo]}
            sowTemplateFileInfo={sowTemplateFileInfo}
          />
        </Tabs.TabPane>

        <Tabs.TabPane
          forceRender
          key={toString(TabKeys.FinancialInfo)}
          tab={
            <div
              className="tab-pane-wrapper"
              data-id="editCustomerAdditionInfoOverviewTab"
            >
              <span className="tab-title">
                {tabs[TabKeys.FinancialInfo].title}
              </span>
              {getTabValidationIcon(tabs[TabKeys.FinancialInfo].status)}
            </div>
          }
        >
          <FinancialInfo
            formRef={financialInfoFormRef}
            tempToPermConversionFileInfo={tempToPermConversionFileInfo}
            purchaseOrderFileInfo={purchaseOrderFile}
            initialValues={initialValues?.financialInfo}
            onChange={onFormChange[TabKeys.FinancialInfo]}
          />
        </Tabs.TabPane>

        <Tabs.TabPane
          forceRender
          key={toString(TabKeys.AssignmentRequirements)}
          tab={
            <div
              className="tab-pane-wrapper"
              data-id="editCustomerAdditionInfoAssignmentsTab"
            >
              <span className="tab-title">
                {tabs[TabKeys.AssignmentRequirements].title}
              </span>
              {getTabValidationIcon(
                tabs[TabKeys.AssignmentRequirements].status,
              )}
            </div>
          }
        >
          <AssignmentRequirements
            formRef={assignmentRequirementsFormRef}
            onboardingDocumentationFileInfo={onboardingDocumentationFileInfo}
            initialValues={initialValues?.assignmentRequirements}
            onChange={onFormChange[TabKeys.AssignmentRequirements]}
          />
        </Tabs.TabPane>
      </Tabs>

      <BaseModalFooter className="button-space-between">
        <div className="d-flex">
          <Button
            className="with-icon modal-icon-btn"
            onClick={handleModalCancel}
          >
            <span className="icon icon-closed-outlined" />
            Cancel
          </Button>
        </div>

        <div className="d-flex">
          {activeTab !== TabKeys.GeneralContactInfo && (
            <Button
              className="with-icon modal-icon-btn"
              onClick={handlePrevStep}
            >
              <span className="icon icon-double-left" />
              Back
            </Button>
          )}

          {activeTab < tabs.length - 1 && (
            <Button
              className="with-icon"
              type="primary"
              onClick={handleNextStep}
            >
              Next
              <span className="icon icon-double-right" />
            </Button>
          )}

          {activeTab === tabs.length - 1 && (
            <Button
              type="primary"
              data-id="editCustomerAdditionInfoBtn"
              disabled={isLoading}
              loading={isLoading}
              onClick={handleSubmit}
            >
              Save
            </Button>
          )}
        </div>
      </BaseModalFooter>
    </BaseModal>
  );
}
